import axios from 'axios';
import { jsonrepair } from 'jsonrepair';
import Session from 'supertokens-web-js/recipe/session';
import { extractTitle } from './utils';

const reqUrl = process.env.NEXT_PUBLIC_MSG_API_URL || 'http://localhost:80';

// Create a new instance of axios
const api = axios.create({
  baseURL: reqUrl,
});

// Add a request interceptor
api.interceptors.request.use(
  async (config) => {
    try {
      // Get the session from SuperTokens
      const session = await Session.getAccessToken();
      if (session) {
        // Add the Authorization header to the request
        config.headers['Authorization'] = `Bearer ${session}`;
      }
    } catch (err) {
      console.error('Error getting session token:', err);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

async function createDoc({
  name,
  document_template_id,
}: {
  name: string;
  document_template_id: number;
}) {
  const { data } = await api.post(
    '/create-document/',
    { name: name, document_template_id: document_template_id },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
  const sortedData = data.document_template.prompt_input_templates.sort(
    (a: any, b: any) => a.id - b.id,
  );
  const returnData = {
    data: sortedData,
    docId: data.id,
  };
  return returnData;
}

async function saveInputs({ inputs }: { inputs: any }) {
  await api.post('/save-prompt-inputs/', inputs, {
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

async function buildDoc({ documentId }: { documentId: number }) {
  await api.post(
    '/build-document/',
    { document_id: documentId },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
}

async function getDocByID({ 
  documentId, 
  type, 
  option, 
  ignorePostprocess,
}: { 
  documentId: number; 
  type?: string; 
  option?: string; 
  ignorePostprocess?: boolean;
 }) {
  const { data } = await api.get(`/get-document/${documentId}/`);
  const isToolGenerated = !!type;
  const keyTrendsDetected = (name: string) => {
    const lowerCaseStr = name.toLowerCase();
    return lowerCaseStr.includes('keytrends') || lowerCaseStr.includes('key trends');
  };
  const titleGenerateDetected = (name: string) => {
    const lowerCaseStr = name.toLowerCase();
    return lowerCaseStr.includes('titlegenerator');
  };

  let answer = data.document_body || '';
  if (
    data.document_body &&
    data.status === 'complete' &&
    !keyTrendsDetected(data.document_template.name) &&
    !titleGenerateDetected(data.document_template.name) &&
    !ignorePostprocess
  ) {
    console.log('Raw response from LLM:\n' + answer);
    if (isToolGenerated) { 
      answer = answer.replace(/###/g, '### ')
      .replace(/\t/g, '\n')
      .replace(/\*\*([^*]+):\s+\*\*/g, '**$1:**');

      if (type === 'cet') {
        answer = answer
          .replace(
            /(\*\*Appeals and Techniques:\*\*)(.*?)(\n|$)/g,
            (_: string, prefix: string, content: string): string => {
              const bulletPoints = content
                .split(',')
                .map((item: string) => `\n- ${item.trim()}`)
                .join('');
              return `${prefix}${bulletPoints}\n`;
            },
          )
          .replace(
            /(\*\*Key Features:\*\*)(.*?)(\n|$)/g,
            (_: string, prefix: string, content: string): string => {
              const bulletPoints = content
                .split(',')
                .map((item: string) => `\n- ${item.trim()}`)
                .join('');
              return `${prefix}${bulletPoints}\n`;
            },
          )
          .replace(
            /(\*\*Product Expansion \d+:\*\*)/g,
            (match: string): string => `### ${match.replace(/\*\*/g, '')}`,
          )
          .replace(/(\*\*Appeals and Techniques:\*\*)/g, '\n$1')
          .replace(/(\*\*Key Features:\*\*)/g, '\n$1')
          .replace(/(\*\*Target Audience:\*\*)/g, '\n$1')
          .replace(/(\*\*Desired Behavioral Objective:\*\*)/g, '\n$1')
          .replace(/(\*\*Supporting Objective:\*\*)/g, '\n$1')
          .replace(/(\*\*Description:\*\*)/g, '\n$1')
          .replace(/(\*\*Product Prompt:\*\*)/g, '\n$1')
          .replace(/(\*\*Product Name:\*\*)/g, '\n$1')
          .replace(/(\*\*Design Features:\*\*)/g, '\n$1')
          .replace(/(\*\*Generative AI Prompt:\*\*)/g, '\n$1')
          .replace(/(\*\*Justification:\*\*)/g, '\n$1');
      }
      if (type === 'aaa' || type === 'asa') {
        if (answer.includes("**Title:")) {
          const pattern = /(\*\*Title):(.*?)\*\*/g;
          answer = answer.replace(pattern, "$1:** $2");
        }
      }
      if (type === 'pse' || type === 'cat'){
        if (answer.includes("{{target}}") || 
            answer.includes("{{appeal}}") || 
            answer.includes("{{technique}}")) 
          {
            answer = null;
          }
      }
      if (type === 'ire') {
        const exceptions = [
          "Appeal:",
          "Technique:",
          "Cognitive Bias:",
          "Justification:",
          "Note:"
        ];
        
        const lines = answer.split('\n');
        const formattedLines = lines.map((line: string) => {
          const trimmedLine = line.trim();
          
          if (trimmedLine.endsWith('**') && !trimmedLine.startsWith('**') && 
              trimmedLine.indexOf('**') === trimmedLine.length - 2) {
            return '**' + line;
          }
          
          const titleMatch = trimmedLine.match(/^\*\*([^:]+):\*\* ([^*]+)\*\*$/);
          if (titleMatch) {
            const titleType = titleMatch[1];
            const content = titleMatch[2];
            return `**${titleType}:** ${content}`;
          }
          
          if (trimmedLine.startsWith("**") || exceptions.some(ex => trimmedLine.startsWith(ex))) {
            return line;
          }
          
          const colonIndex = trimmedLine.indexOf(":");
          if (colonIndex > 0) {
            const beforeColon = trimmedLine.substring(0, colonIndex);
            if (/^[A-Za-z]/.test(beforeColon)) {
              const afterColon = trimmedLine.substring(colonIndex + 1);
              return `**${beforeColon}:**${afterColon}`;
            }
          }
          
          return line;
        });
        answer = formattedLines.join('\n');
        
        answer = answer
          .replace(/Appeal:/g, '\n\nAppeal:')
          .replace(/Technique:/g, '\n\nTechnique:')
          .replace(/Cognitive Bias:/g, '\n\nCognitive Bias:')
          .replace(/Justification:/g, '\n\nJustification:')
          .replace(/\n\n\n/g, '\n\n');
      }
      if (type === 'lpg') {
        answer = answer.replace(/Question \d+:/g, (match: any) => `**${match}**`);
      }
    } else {
      answer = answer.replace(/((?:<\|start_header_id\|>)+)assistant<\|end_header_id\|>/g, '');
      answer = answer.replaceAll('”', '\"');
      answer = answer.replaceAll('“', '\"');
      const excludeStrings = [
        `Here's the JSON object based on the provided structured text document:`,
        'Since there is no user_feedback, I will create a new SPO that is entirely new and different from the bad_spo and good_spos.\n\nHere is the new SPO:',
        'Since the user_feedback is null, I will create a new SPO that is entirely new and not similar to the bad_spo or any good_spo.\n\nHere is the new SPO:',
        '```',
        '\r\nSince there is no user_feedback provided, I will generate a new_ta that is entirely different from the bad_tas and good_tas. Here is the output in JSON format:\n\n\n',
        '\r\nSince there is no user_feedback provided, I will create a new set of TAs that are entirely new and not similar to the bad_tas or good_tas. Here is the output in JSON format:',
        '/*\r\nSince there is no user_feedback provided, I will create a new set of TAs according to the guidance provided.\r\n\r\nHere is the JSON output:',
        '\r\nSince the user_feedback is empty, I will completely replace the bad_rta with a new_rta. The bad_rta is from the all_secondary list, so I will replace it with a new_secondary_rta.\r\n\r\nHere is the output in JSON format:',
        '\n\nSince the user_feedback is not provided, I will assume it is null. \n\nThe bad_rta is from the all_primary list. \n\nThe new_rta will replace the bad_rta on the all_primary list.',
        '\n\nSince there is no user_feedback provided, I will assume that the bad_rta needs to be replaced with a new_rta.',
        '\n\nSince the user_feedback is null, I will create a new_ta by generating an entirely new TA that is not similar to the bad_tas or good_tas.',
        '\n\nSince the user_feedback is null, I will create a new set of TAs that are entirely different from the bad_tas and good_tas.',
        '\n\nSince there is no user_feedback provided, I will create a new set of TAs that are entirely different from the bad_tas and good_tas.',
        '\n\nSince the user_feedback is null, I will create a new set of target audiences (new_tas) that are entirely different from the bad_tas and good_tas.',
        '\n\nSince the user_feedback is null, I will create an equivalent number of new_tas as the number of bad_tas above in the same JSON format.',
        '\n\nSince the user_feedback is null, I will create an entirely new set of target audiences (new_tas) that are not similar to the bad_tas or good_tas.',
        '\n\nSince the user_feedback is null, I will create an entirely new TA, following the guidance above.',
        '\n\nSince the user_feedback is null, I will create a new set of TAs that are entirely new and not similar to the bad_tas or good_tas.',
        '\n\nHere is the JSON output with the new TAs created based on the guidance:',
        'Here is the JSON object:',
        'Here is the JSON object response:',
        'Here is the JSON output:',
        'Here is the revised JSON output:',
        'Here is the new_ta:',
        'Here is the output:',
        'Here is the generated JSON object:',
        'Here is the new JSON object:',
      ];
      excludeStrings.forEach((excludeString) => {
        answer = answer.replaceAll(excludeString, '');
      });
      answer = answer.replace(/}\s*},\s*"([1-8])/g, '\n},\n"$1'); //fix extra closing braces
      answer = answer.replace(/",*\s*"([1-8])/g, '"\n},\n"$1'); //fix missing closing braces
      answer = answer.replace(/\\\r?\n/g, '\\n'); //convert literal line breaks preceded by backslashes to escaped line breaks
      answer = answer.replace(
        /(\\n[^\S\r\n]*(?:\r?\n)+[^\S\r\n]*)|(^[^\S\r\n]*(?:\r?\n)+[^\S\r\n]*\\n)/gm,
        (match: string, g1: string | undefined, g2: string | undefined): string => {
          if (g1) return '\\n';
          if (g2) return '\\n';
          return match;
        },
      ); //fix extra literal line breaks

      const jsonObjects = splitJsonObjects(answer);
      const processedJsons = jsonObjects.map(jsonString => {
        try {
          const processed = processIndividualJson(jsonString);
          JSON.parse(processed);
          return processed;
        } catch (error) {
          console.error('Invalid JSON produced:', error);
          return jsonString;
        }
      }).filter(Boolean);      
      answer = processedJsons.map(json => json.trim()).join('\n==========\n');

    }
    console.log(`Processed response for document id ${documentId}:\n${answer}`);
  }

  if (
    data.document_body &&
    data.status === 'complete' &&
    keyTrendsDetected(data.document_template.name)
  ) {
    const trendsRegex = new RegExp(`Key Trends\\s+v1`, 'g');
    const newAnswer = answer.replace(trendsRegex, 'Key Trends');
    answer = newAnswer;
  }
  const reportTitle = extractTitle(answer) || data.name;
  const returnData = {
    status: data.status,
    statusMessage: data.prompt_responses_status,
    fullResponse: data,
    output: {
      answer: answer,
    },
    title: reportTitle,
    type,
    option,
    promptInputs: data.prompt_inputs,
    isOldSchema: data.document_template.id === 8, 
  };
  return returnData;
}

export const chats = {
  createDoc,
  saveInputs,
  buildDoc,
  getDocByID,
};


function processIndividualJson(jsonString: string): string {
  try {
    // Clean up and repair the JSON
    let cleanedJson = jsonString.trim();
    cleanedJson = cleanedJson.replace(/\\\r?\n/g, '\\n');
    cleanedJson = cleanedJson.replace(
      /(\\n[^\S\r\n]*(?:\r?\n)+[^\S\r\n]*)|(^[^\S\r\n]*(?:\r?\n)+[^\S\r\n]*\\n)/gm,
      (match: string, g1: string | undefined, g2: string | undefined): string => {
        if (g1) return '\\n';
        if (g2) return '\\n';
        return match;
      }
    );
    cleanedJson = cleanedJson.replace(/\r?\n(?!["\[\]{}\s])/g, '\\n');
    cleanedJson = cleanedJson.replace(/\r?\n(?!["\[\]{}\s])/g, '\\n');
    cleanedJson = cleanedJson.replace(/},\s*"(\d+\.\s)/g, ',\n"$1');
    cleanedJson = cleanedJson.replace(/\s*}\s*("|)\s*(,|)(},\s*"(\d)":)/g, ',"$2":');

    // Repair and parse JSON
    cleanedJson = jsonrepair(cleanedJson);
    let jsonObject = JSON.parse(cleanedJson);

    // Convert arrays to strings
    jsonObject = convertArraysToStrings(jsonObject);

    // Interpolate line breaks
    const interpolateLineBreaks = (obj: any): any => {
      if (typeof obj === 'string') {
        return obj.replace(
          /([^\n])(\n?)(?<![,.\d])(?<!\()(?<=\.\s|\n)(\d{1,2}\.|\(\d{1,2}\)|\d{1,2}\))\s/g,
          (match: string, before: string, existingBreak: string, point: string) => {
            const num = parseInt(point);
            return (existingBreak || num >= 20) ? match : `${before}\n${point} `;
          },
        );
      } else if (typeof obj === 'object' && obj !== null) {
        Object.keys(obj).forEach((key) => {
          obj[key] = interpolateLineBreaks(obj[key]);
        });
      }
      return obj;
    };
    jsonObject = interpolateLineBreaks(jsonObject);

    // Convert back to string
    return JSON.stringify(jsonObject);
  } catch (error) {
    console.error('Error processing individual JSON:', error);
    return jsonString; // Return original string if processing fails
  }
}

// Function to split response into individual JSONs
function splitJsonObjects(text: string): string[] {
  // Split on the delimiter '==========' and filter out empty strings
  return text.split('==========')
    .map(str => str.trim())
    .filter(str => str.length > 0);
}

function convertArraysToStrings(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map((item) => convertArraysToStrings(item));
  } else if (typeof obj === 'object' && obj !== null) {
    const newObj: any = {};
    const singleLetterRegex = /^[a-z]$/;
    for (const [key, value] of Object.entries(obj)) {
      if (Array.isArray(value) && !["spos", "primary", "secondary"].includes(key)) {
        newObj[key] = value.map((item, index) => {
          if (typeof item === 'object' && item !== null) {
            return JSON.stringify(convertArraysToStrings(item));
          }
          const hasNumberPrefix = /^\d+\.\s/.test(String(item));
          const stringValue = String(item);
          return hasNumberPrefix ? stringValue : `${index + 1}. ${stringValue}`;
        }).join('\n');
      } else if (typeof value === 'object' && value !== null && singleLetterRegex.test(key)) {
        // Handle single lowercase letter keys with object values
        newObj[key] = Object.entries(value)
          .map(([subKey, subValue]) => `${subKey}: ${subValue}`)
          .join('\n');
      } else {
        newObj[key] = convertArraysToStrings(value);
      }
    }
    return newObj;
  } else if (Number.isInteger(obj)) {
    return obj.toString();
  }
  return obj;
}

